import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", service: "", message: "" };

export const Contact = () => {
  const { t } = useI18n();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error(t.contact.form.error_required);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim() || null,
        service: form.service || null,
      });
      toast.success(t.contact.form.success);
      setForm(initial);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        t.contact.form.error_generic;
      toast.error(typeof msg === "string" ? msg : t.contact.form.error_generic);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-muted/30 border-t border-border"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <span className="overline text-muted-foreground">
              {t.contact.overline}
            </span>
            <h2
              data-testid="contact-heading"
              className="mt-5 font-heading font-semibold text-4xl md:text-5xl leading-[1.05] tracking-tight text-foreground"
            >
              {t.contact.heading_1}{" "}
              <span className="italic font-light text-primary">
                {t.contact.heading_em}
              </span>{" "}
              {t.contact.heading_2}
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-md">
              {t.contact.subtitle}
            </p>

            <div className="mt-12 space-y-5">
              <ContactLine
                icon={Mail}
                label={t.contact.email_label}
                value="dbrinvest@email.cz"
                href="mailto:dbrinvest@email.cz"
                testid="contact-email"
              />
              <ContactLine
                icon={Phone}
                label={t.contact.phone_label}
                value="+420 774 452 773"
                href="tel:+420774452773"
                testid="contact-phone"
              />
              <ContactLine
                icon={MapPin}
                label={t.contact.address_label}
                value={t.contact.address_value}
                testid="contact-address"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-card border border-border rounded-sm p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              <Input
                label={t.contact.form.name}
                value={form.name}
                onChange={update("name")}
                testid="input-name"
                placeholder={t.contact.form.name_placeholder}
              />
              <Input
                label={t.contact.form.email}
                type="email"
                value={form.email}
                onChange={update("email")}
                testid="input-email"
                placeholder={t.contact.form.email_placeholder}
              />
              <Input
                label={t.contact.form.phone}
                value={form.phone}
                onChange={update("phone")}
                testid="input-phone"
                placeholder={t.contact.form.phone_placeholder}
              />
              <div>
                <label className="overline text-muted-foreground block mb-3">
                  {t.contact.form.service}
                </label>
                <select
                  value={form.service}
                  onChange={update("service")}
                  data-testid="input-service"
                  className="w-full bg-transparent border-b border-border py-3 text-base text-foreground focus:outline-none focus:border-primary transition-colors"
                >
                  <option value="">{t.contact.form.service_placeholder}</option>
                  {t.contact.services_list.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="overline text-muted-foreground block mb-3">
                  {t.contact.form.message}
                </label>
                <textarea
                  value={form.message}
                  onChange={update("message")}
                  data-testid="input-message"
                  rows={4}
                  placeholder={t.contact.form.message_placeholder}
                  className="w-full bg-transparent border-b border-border py-3 text-base text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary transition-colors resize-none"
                />
              </div>

              <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-4">
                <p className="text-xs text-muted-foreground max-w-sm">
                  {t.contact.form.gdpr}
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-primary text-primary-foreground rounded-full text-sm font-medium hover:bg-foreground transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {loading ? t.contact.form.submitting : t.contact.form.submit}
                  {!loading && <ArrowRight className="w-4 h-4" />}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Input = ({ label, testid, ...props }) => (
  <div>
    <label className="overline text-muted-foreground block mb-3">{label}</label>
    <input
      {...props}
      data-testid={testid}
      className="w-full bg-transparent border-b border-border py-3 text-base text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary transition-colors"
    />
  </div>
);

const ContactLine = ({ icon: Icon, label, value, href, testid }) => {
  const Tag = href ? "a" : "div";
  return (
    <Tag
      href={href}
      data-testid={testid}
      className="flex items-start gap-4 group"
    >
      <span className="mt-1 w-10 h-10 rounded-sm bg-primary/5 text-primary inline-flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
        <Icon className="w-4 h-4" />
      </span>
      <div>
        <div className="overline text-muted-foreground">{label}</div>
        <div className="mt-1 text-base md:text-lg text-foreground">{value}</div>
      </div>
    </Tag>
  );
};

export default Contact;
